/**
 * Recording Store — IndexedDB persistence for crash recovery (WS6)
 *
 * Saves audio chunks to IndexedDB every second (both PSA + Whisper streams).
 * On page load, checks for orphaned sessions and offers recovery.
 * Auto-purges stale recordings after 24 hours.
 */

const DB_NAME = 'snpod-recordings'
const DB_VERSION = 1
const STORE_SESSIONS = 'sessions'
const STORE_CHUNKS = 'chunks'
const STALE_HOURS = 24

interface RecordingSession {
  id: string
  startedAt: number
  sysId: string | null
  table: string | null
  recordDescription: string | null
  captureMode: string
  psaMimeType: string
  whisperMimeType: string | null
  durationSeconds: number
}

interface AudioChunk {
  sessionId: string
  stream: 'psa' | 'whisper'
  index: number
  data: Blob
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
        const store = db.createObjectStore(STORE_CHUNKS, { autoIncrement: true })
        store.createIndex('sessionId', 'sessionId', { unique: false })
        store.createIndex('sessionStream', ['sessionId', 'stream'], { unique: false })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

// --- Session management ---

export async function createSession(session: RecordingSession): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readwrite')
    tx.objectStore(STORE_SESSIONS).put(session)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function updateSessionDuration(sessionId: string, durationSeconds: number): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readwrite')
    const store = tx.objectStore(STORE_SESSIONS)
    const get = store.get(sessionId)
    get.onsuccess = () => {
      if (get.result) {
        get.result.durationSeconds = durationSeconds
        store.put(get.result)
      }
    }
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

export async function deleteSession(sessionId: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STORE_SESSIONS, STORE_CHUNKS], 'readwrite')

    // Delete session
    tx.objectStore(STORE_SESSIONS).delete(sessionId)

    // Delete all chunks for this session
    const chunkStore = tx.objectStore(STORE_CHUNKS)
    const index = chunkStore.index('sessionId')
    const cursor = index.openCursor(IDBKeyRange.only(sessionId))
    cursor.onsuccess = () => {
      const result = cursor.result
      if (result) {
        result.delete()
        result.continue()
      }
    }

    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// --- Chunk storage ---

export async function saveChunk(chunk: AudioChunk): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CHUNKS, 'readwrite')
    tx.objectStore(STORE_CHUNKS).add(chunk)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// --- Recovery ---

export interface RecoverableSession {
  session: RecordingSession
  psaBlob: Blob | null
  whisperBlob: Blob | null
}

export async function getRecoverableSessions(): Promise<RecoverableSession[]> {
  const db = await openDB()

  // Get all sessions
  const sessions: RecordingSession[] = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly')
    const request = tx.objectStore(STORE_SESSIONS).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  if (sessions.length === 0) return []

  const results: RecoverableSession[] = []

  for (const session of sessions) {
    // Skip stale sessions (>24 hours old)
    const ageHours = (Date.now() - session.startedAt) / 1000 / 3600
    if (ageHours > STALE_HOURS) {
      await deleteSession(session.id)
      continue
    }

    // Reconstruct blobs from chunks
    const psaChunks = await getChunksForStream(db, session.id, 'psa')
    const whisperChunks = await getChunksForStream(db, session.id, 'whisper')

    const psaBlob = psaChunks.length > 0
      ? new Blob(psaChunks, { type: session.psaMimeType })
      : null

    const whisperBlob = whisperChunks.length > 0 && session.whisperMimeType
      ? new Blob(whisperChunks, { type: session.whisperMimeType })
      : null

    if (psaBlob) {
      results.push({ session, psaBlob, whisperBlob })
    } else {
      // No chunks — orphaned session with no data, clean up
      await deleteSession(session.id)
    }
  }

  return results
}

function getChunksForStream(db: IDBDatabase, sessionId: string, stream: 'psa' | 'whisper'): Promise<Blob[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CHUNKS, 'readonly')
    const index = tx.objectStore(STORE_CHUNKS).index('sessionStream')
    const request = index.getAll([sessionId, stream])
    request.onsuccess = () => {
      const chunks = (request.result as AudioChunk[])
        .sort((a, b) => a.index - b.index)
        .map(c => c.data)
      resolve(chunks)
    }
    request.onerror = () => reject(request.error)
  })
}

// --- Cleanup ---

export async function purgeStaleRecordings(): Promise<number> {
  const db = await openDB()
  const sessions: RecordingSession[] = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly')
    const request = tx.objectStore(STORE_SESSIONS).getAll()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

  let purged = 0
  for (const session of sessions) {
    const ageHours = (Date.now() - session.startedAt) / 1000 / 3600
    if (ageHours > STALE_HOURS) {
      await deleteSession(session.id)
      purged++
    }
  }
  return purged
}
